import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import MDEditor from '@uiw/react-md-editor';
import { Check, X, Pencil, Trash2, Plus, Search, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';

const COLORS = {
  primary: '#3b82f6',
  primaryHover: '#2563eb',
  success: '#16a34a',
  successHover: '#15803d',
  successLight: '#f0fdf4',
  danger: '#dc2626',
  dangerHover: '#b91c1c',
  neutral: '#6b7280',
  neutralHover: '#4b5563',
  border: '#e5e7eb',
  borderFocus: '#3b82f6',
  bgEditing: '#fefce8',
  bgNew: '#f0fdf4',
  bgHeader: '#f9fafb',
  bgAlt: '#f9fafb',
  text: '#1f2937',
  textMuted: '#6b7280',
};

const inputBase = {
  width: '100%',
  padding: '8px 12px',
  border: `1.5px solid ${COLORS.border}`,
  borderRadius: '6px',
  fontSize: '13px',
  lineHeight: '1.5',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  outline: 'none',
  backgroundColor: '#fff',
};

const inputNewBase = {
  ...inputBase,
  backgroundColor: COLORS.bgNew,
  border: `1.5px solid ${COLORS.success}`,
};

const focusStyle = {
  borderColor: COLORS.borderFocus,
  boxShadow: '0 0 0 3px rgba(59,130,246,0.15)',
};

const focusStyleGreen = {
  borderColor: COLORS.success,
  boxShadow: '0 0 0 3px rgba(22,163,74,0.15)',
};

const blurStyle = {
  borderColor: COLORS.border,
  boxShadow: 'none',
};

const handleFocus = (e) => {
  Object.assign(e.target.style, focusStyle);
};
const handleBlur = (e) => {
  Object.assign(e.target.style, blurStyle);
};
const handleFocusGreen = (e) => {
  Object.assign(e.target.style, focusStyleGreen);
};
const handleBlurGreen = (e) => {
  Object.assign(e.target.style, { borderColor: COLORS.success, boxShadow: 'none' });
};

const HoverButton = ({ children, bgColor, hoverColor, outlined, outlinedColor, onClick, disabled, style: extraStyle }) => {
  const baseStyle = outlined
    ? {
        padding: '7px 14px', color: outlinedColor || COLORS.danger, backgroundColor: 'transparent',
        border: `1.5px solid ${outlinedColor || COLORS.danger}`, borderRadius: '6px', cursor: 'pointer',
        fontSize: '13px', fontWeight: '500', transition: 'all 0.15s ease',
        display: 'inline-flex', alignItems: 'center', gap: '5px', ...extraStyle,
      }
    : {
        padding: '7px 14px', color: 'white', backgroundColor: bgColor,
        border: 'none', borderRadius: '6px', cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: '13px', fontWeight: '500', transition: 'all 0.15s ease',
        display: 'inline-flex', alignItems: 'center', gap: '5px',
        opacity: disabled ? 0.6 : 1, ...extraStyle,
      };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={baseStyle}
      onMouseEnter={(e) => {
        if (disabled) return;
        if (outlined) {
          e.currentTarget.style.backgroundColor = outlinedColor || COLORS.danger;
          e.currentTarget.style.color = 'white';
        } else {
          e.currentTarget.style.backgroundColor = hoverColor;
        }
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        if (outlined) {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = outlinedColor || COLORS.danger;
        } else {
          e.currentTarget.style.backgroundColor = bgColor;
        }
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {children}
    </button>
  );
};

// Extracted as a proper component so it has stable identity and inputs keep focus
const EditableCell = ({ field, value, rowId, editingId, editDataRef, editableFields, onChange, colRender }) => {
  if (editingId !== rowId) {
    if (colRender) return colRender(value);
    const fieldConfig = editableFields.find((f) => f.key === field);
    if (fieldConfig?.type === 'markdown' && value) {
      const truncated = value.length > 100 ? value.substring(0, 100) + '...' : value;
      return <span style={{ whiteSpace: 'pre-wrap', fontSize: '12px', color: COLORS.text }}>{truncated}</span>;
    }
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value);
    }
    return value || <span style={{ color: '#d1d5db' }}>-</span>;
  }

  const fieldConfig = editableFields.find((f) => f.key === field);
  const editData = editDataRef.current;
  const currentValue = editData[field] ?? value;

  switch (fieldConfig?.type) {
    case 'custom':
      return fieldConfig.renderEdit(currentValue, (val) => onChange(field, val));
    case 'markdown':
      return (
        <div data-color-mode="light" style={{ minWidth: '400px' }}>
          <MDEditor
            value={currentValue || ''}
            onChange={(val) => onChange(field, val || '')}
            height={parseInt(fieldConfig.minHeight) || 200}
            preview="edit"
          />
        </div>
      );
    case 'textarea': {
      const displayValue = (typeof currentValue === 'object' && currentValue !== null)
        ? JSON.stringify(currentValue, null, 2)
        : (currentValue || '');
      return (
        <textarea
          value={displayValue}
          onChange={(e) => onChange(field, e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            ...inputBase,
            minHeight: fieldConfig.minHeight || '60px',
            resize: 'vertical',
          }}
        />
      );
    }
    case 'number':
      return (
        <input
          type="number"
          value={currentValue ?? ''}
          onChange={(e) => onChange(field, parseInt(e.target.value) || 0)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={inputBase}
        />
      );
    case 'select': {
      const options = fieldConfig.options || [];
      const val = currentValue ?? '';
      return (
        <select
          value={val}
          onChange={(e) => onChange(field, e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{ ...inputBase, cursor: 'pointer' }}
        >
          {(!fieldConfig.required || !val) && (
            <option value="">{fieldConfig.placeholder || '— vyber —'}</option>
          )}
          {/* Zachová stávající hodnotu, i kdyby v aktuální nabídce chyběla. */}
          {val && !options.includes(val) && <option value={val}>{val}</option>}
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    default:
      return (
        <input
          type="text"
          value={currentValue || ''}
          onChange={(e) => onChange(field, e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={inputBase}
        />
      );
  }
};

const ActionCell = ({ row, editingId, onSave, onCancel, onEdit, onDelete }) => {
  if (editingId === row.id) {
    return (
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        <HoverButton bgColor={COLORS.success} hoverColor={COLORS.successHover} onClick={() => onSave(row.id)}>
          <Check size={14} /> Save
        </HoverButton>
        <HoverButton bgColor={COLORS.neutral} hoverColor={COLORS.neutralHover} onClick={onCancel}>
          <X size={14} /> Cancel
        </HoverButton>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      <HoverButton bgColor={COLORS.primary} hoverColor={COLORS.primaryHover} onClick={() => onEdit(row)}>
        <Pencil size={14} /> Edit
      </HoverButton>
      {onDelete && (
        <HoverButton outlined onClick={() => onDelete(row.id)}>
          <Trash2 size={14} /> Delete
        </HoverButton>
      )}
    </div>
  );
};

const AdminTable = ({
  data,
  columns,
  onSave,
  onCreate,
  onDelete,
  editableFields,
  title,
  filterField = 'topic',
  uniqueFilterValues = [],
  loading = false,
  emptyMessage = 'No data found.',
  extraButtons = null,
  selectable = false,
  onSelectionChange = null,
}) => {
  const [editingId, setEditingId] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());

  useEffect(() => {
    setSelectedRows(new Set());
    if (onSelectionChange) onSelectionChange(new Set());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  const [editData, setEditData] = useState({});
  const editDataRef = useRef(editData);
  editDataRef.current = editData;
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newItemData, setNewItemData] = useState({});
  const newItemDataRef = useRef(newItemData);
  newItemDataRef.current = newItemData;

  const handleEdit = (row) => {
    setEditingId(row.id);
    const initialData = {};
    editableFields.forEach((field) => {
      const rawValue = row[field.key];
      initialData[field.key] = field.deserialize ? field.deserialize(rawValue) : rawValue;
    });
    setEditData(initialData);
  };

  const handleSave = async (id) => {
    try {
      const serializedData = {};
      Object.keys(editData).forEach((key) => {
        const fieldConfig = editableFields.find((f) => f.key === key);
        serializedData[key] = fieldConfig?.serialize ? fieldConfig.serialize(editData[key]) : editData[key];
      });
      await onSave(id, serializedData);
      setEditingId(null);
      setEditData({});
    } catch (err) {
      console.error('Error saving:', err);
      alert('Failed to save: ' + err.message);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDelete = async (id) => {
    if (!onDelete) return;
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await onDelete(id);
      } catch (err) {
        console.error('Error deleting:', err);
        alert('Failed to delete: ' + err.message);
      }
    }
  };

  const handleStartAddNew = () => {
    setIsAddingNew(true);
    const initialData = {};
    editableFields.forEach((field) => {
      const rawDefault = field.type === 'number' ? (field.default ?? 1) : (field.default ?? '');
      initialData[field.key] = field.deserialize ? field.deserialize(rawDefault) : rawDefault;
    });
    setNewItemData(initialData);
  };

  const handleCancelAddNew = () => {
    setIsAddingNew(false);
    setNewItemData({});
  };

  const handleSaveNew = async () => {
    if (!onCreate) return;
    try {
      const serializedData = {};
      Object.keys(newItemData).forEach((key) => {
        const fieldConfig = editableFields.find((f) => f.key === key);
        serializedData[key] = fieldConfig?.serialize ? fieldConfig.serialize(newItemData[key]) : newItemData[key];
      });
      await onCreate(serializedData);
      setIsAddingNew(false);
      setNewItemData({});
    } catch (err) {
      console.error('Error creating:', err);
      alert('Failed to create: ' + err.message);
    }
  };

  const handleNewItemChange = (field, value) => {
    setNewItemData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderNewItemCell = (field) => {
    const fieldConfig = editableFields.find((f) => f.key === field);
    if (!fieldConfig) return '-';

    const currentValue = newItemData[field] ?? '';

    switch (fieldConfig.type) {
      case 'custom':
        return fieldConfig.renderEdit(currentValue, (val) => handleNewItemChange(field, val));
      case 'markdown':
        return (
          <div data-color-mode="light">
            <MDEditor
              value={currentValue}
              onChange={(val) => handleNewItemChange(field, val || '')}
              height={parseInt(fieldConfig.minHeight) || 200}
              preview="edit"
            />
          </div>
        );
      case 'textarea':
        return (
          <textarea
            value={currentValue}
            onChange={(e) => handleNewItemChange(field, e.target.value)}
            placeholder={fieldConfig.placeholder || field}
            onFocus={handleFocusGreen}
            onBlur={handleBlurGreen}
            style={{
              ...inputNewBase,
              minHeight: fieldConfig.minHeight || '60px',
              resize: 'vertical',
            }}
          />
        );
      case 'number':
        return (
          <input
            type="number"
            value={currentValue}
            onChange={(e) => handleNewItemChange(field, parseInt(e.target.value) || 0)}
            placeholder={fieldConfig.placeholder || field}
            onFocus={handleFocusGreen}
            onBlur={handleBlurGreen}
            style={inputNewBase}
          />
        );
      case 'select': {
        const options = fieldConfig.options || [];
        return (
          <select
            value={currentValue || ''}
            onChange={(e) => handleNewItemChange(field, e.target.value)}
            onFocus={handleFocusGreen}
            onBlur={handleBlurGreen}
            style={{ ...inputNewBase, cursor: 'pointer' }}
          >
            <option value="">{fieldConfig.placeholder || '— vyber —'}</option>
            {options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      }
      default:
        return (
          <input
            type="text"
            value={currentValue}
            onChange={(e) => handleNewItemChange(field, e.target.value)}
            placeholder={fieldConfig.placeholder || field}
            onFocus={handleFocusGreen}
            onBlur={handleBlurGreen}
            style={inputNewBase}
          />
        );
    }
  };

  const toggleRow = (id) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      if (onSelectionChange) onSelectionChange(next);
      return next;
    });
  };

  const toggleAll = (rows) => {
    setSelectedRows((prev) => {
      const allIds = rows.map((r) => r.original.id);
      const allSelected = allIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) allIds.forEach((id) => next.delete(id));
      else allIds.forEach((id) => next.add(id));
      if (onSelectionChange) onSelectionChange(next);
      return next;
    });
  };

  // Column definitions are now stable — editData is NOT in dependencies.
  // EditableCell and ActionCell are proper components that receive changing props
  // without causing column identity to change (which would unmount inputs).
  const tableColumns = useMemo(() => {
    const cols = columns.map((col) => ({
      accessorKey: col.key,
      header: ({ column }) => (
        <div
          style={{ cursor: col.sortable !== false ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '4px', userSelect: 'none' }}
          onClick={() => col.sortable !== false && column.toggleSorting()}
        >
          {col.header}
          {col.sortable !== false && (
            <span style={{ opacity: 0.4, fontSize: '11px' }}>
              {column.getIsSorted() === 'asc' ? ' \u25B2' : column.getIsSorted() === 'desc' ? ' \u25BC' : ' \u21C5'}
            </span>
          )}
        </div>
      ),
      cell: ({ row, getValue }) => {
        const value = getValue();
        if (col.editable) {
          return (
            <EditableCell
              field={col.key}
              value={value}
              rowId={row.original.id}
              editingId={editingId}
              editDataRef={editDataRef}
              editableFields={editableFields}
              onChange={handleChange}
              colRender={col.render}
            />
          );
        }
        if (col.render) {
          return col.render(value, row.original);
        }
        return value || <span style={{ color: '#d1d5db' }}>-</span>;
      },
      size: col.width,
    }));

    cols.push({
      id: 'actions',
      header: 'Actions',
      enableResizing: false,
      cell: ({ row }) => (
        <ActionCell
          row={row.original}
          editingId={editingId}
          onSave={handleSave}
          onCancel={handleCancel}
          onEdit={handleEdit}
          onDelete={onDelete ? handleDelete : null}
        />
      ),
      size: 180,
    });

    if (selectable) {
      cols.unshift({
        id: 'select',
        header: ({ table }) => {
          const rows = table.getRowModel().rows;
          const allSelected = rows.length > 0 && rows.every((r) => selectedRows.has(r.original.id));
          return (
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => toggleAll(rows)}
              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
            />
          );
        },
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedRows.has(row.original.id)}
            onChange={() => toggleRow(row.original.id)}
            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
          />
        ),
        enableResizing: false,
        size: 48,
      });
    }

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, editingId, editableFields, onDelete, selectable, selectedRows]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    columnResizeMode: 'onChange',
    state: {
      sorting,
      globalFilter,
      columnFilters,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
  });

  const handleFilterChange = (value) => {
    if (value) {
      setColumnFilters([{ id: filterField, value }]);
    } else {
      setColumnFilters([]);
    }
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, color: COLORS.text, fontSize: '1.3rem', fontWeight: '700' }}>{title}</h2>

        {extraButtons}

        {onCreate && !isAddingNew && (
          <HoverButton bgColor={COLORS.success} hoverColor={COLORS.successHover} onClick={handleStartAddNew}
            style={{ padding: '8px 16px', fontSize: '14px', fontWeight: '600' }}>
            <Plus size={16} /> Add New
          </HoverButton>
        )}

        {uniqueFilterValues.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: COLORS.textMuted, fontSize: '13px' }}>
              Filter by {filterField}:
            </span>
            <select
              onChange={(e) => handleFilterChange(e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              style={{
                ...inputBase,
                width: 'auto',
                padding: '7px 12px',
                cursor: 'pointer',
              }}
            >
              <option value="">All</option>
              {uniqueFilterValues.map((val) => (
                <option key={val} value={val}>{val}</option>
              ))}
            </select>
          </div>
        )}

        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: COLORS.textMuted, pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Search..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            style={{
              ...inputBase,
              width: '220px',
              paddingLeft: '36px',
            }}
          />
        </div>

        <span style={{
          color: COLORS.textMuted, fontSize: '13px', backgroundColor: COLORS.bgHeader,
          padding: '5px 12px', borderRadius: '20px',
        }}>
          {table.getFilteredRowModel().rows.length} items
        </span>
      </div>

      {/* Add New Form */}
      {isAddingNew && (
        <div style={{
          marginBottom: '20px', padding: '24px', backgroundColor: COLORS.bgNew,
          borderTop: `3px solid ${COLORS.success}`, borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
          <h3 style={{ margin: '0 0 20px 0', color: COLORS.success, fontSize: '1.1rem', fontWeight: '600' }}>Add New Item</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            {editableFields.map((field) => (
              <div key={field.key} style={{
                ...(field.type === 'markdown' || field.type === 'textarea' || field.type === 'custom'
                  ? { gridColumn: '1 / -1' } : {}),
              }}>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  marginBottom: '6px', fontWeight: '600', color: COLORS.text, fontSize: '13px',
                }}>
                  {field.required && (
                    <span style={{
                      width: '6px', height: '6px', borderRadius: '50%',
                      backgroundColor: COLORS.danger, flexShrink: 0,
                    }} />
                  )}
                  {field.label || field.key}
                </label>
                {renderNewItemCell(field.key)}
              </div>
            ))}
          </div>
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <HoverButton bgColor={COLORS.success} hoverColor={COLORS.successHover} onClick={handleSaveNew}
              style={{ padding: '10px 24px', fontSize: '14px', fontWeight: '600' }}>
              <Check size={16} /> Save New Item
            </HoverButton>
            <HoverButton bgColor={COLORS.neutral} hoverColor={COLORS.neutralHover} onClick={handleCancelAddNew}
              style={{ padding: '10px 24px', fontSize: '14px' }}>
              <X size={16} /> Cancel
            </HoverButton>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textMuted }}>Loading...</div>
      ) : (
        <>
          {/* Table */}
          <div style={{ overflowX: 'auto', borderRadius: '8px', border: `1px solid ${COLORS.border}` }}>
            <table
              style={{
                width: table.getCenterTotalSize(),
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                backgroundColor: 'white',
              }}
            >
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} style={{ backgroundColor: COLORS.bgHeader }}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          padding: '12px 16px', textAlign: 'left',
                          borderBottom: `2px solid ${COLORS.border}`,
                          width: header.getSize(),
                          fontSize: '12px', fontWeight: '600', textTransform: 'uppercase',
                          letterSpacing: '0.05em', color: COLORS.textMuted,
                          position: 'sticky', top: 0, zIndex: 10, backgroundColor: COLORS.bgHeader,
                        }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanResize() && (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            style={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              height: '100%',
                              width: '5px',
                              cursor: 'col-resize',
                              userSelect: 'none',
                              touchAction: 'none',
                              backgroundColor: header.column.getIsResizing()
                                ? COLORS.primary
                                : 'transparent',
                            }}
                            onMouseEnter={(e) => {
                              if (!header.column.getIsResizing()) {
                                e.currentTarget.style.backgroundColor = COLORS.border;
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!header.column.getIsResizing()) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          />
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, index) => {
                  const isEditing = editingId === row.original.id;
                  const isSelected = selectable && selectedRows.has(row.original.id);
                  return (
                    <tr
                      key={row.id}
                      style={{
                        backgroundColor: isEditing ? COLORS.bgEditing : isSelected ? '#eff6ff' : (index % 2 === 0 ? '#fff' : COLORS.bgAlt),
                        borderBottom: `1px solid ${COLORS.border}`,
                        boxShadow: isEditing ? 'inset 4px 0 0 #f59e0b' : isSelected ? 'inset 4px 0 0 #3b82f6' : 'none',
                        transition: 'background-color 0.15s ease',
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} style={{ padding: '12px 16px', verticalAlign: 'top', width: cell.column.getSize(), overflow: 'hidden' }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginTop: '16px', padding: '12px 16px', backgroundColor: COLORS.bgHeader,
              borderRadius: '8px', border: `1px solid ${COLORS.border}`,
            }}
          >
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {[
                { icon: <ChevronsLeft size={16} />, onClick: () => table.setPageIndex(0), disabled: !table.getCanPreviousPage() },
                { icon: <ChevronLeft size={16} />, onClick: () => table.previousPage(), disabled: !table.getCanPreviousPage() },
                { icon: <ChevronRight size={16} />, onClick: () => table.nextPage(), disabled: !table.getCanNextPage() },
                { icon: <ChevronsRight size={16} />, onClick: () => table.setPageIndex(table.getPageCount() - 1), disabled: !table.getCanNextPage() },
              ].map((btn, i) => (
                <button
                  key={i}
                  onClick={btn.onClick}
                  disabled={btn.disabled}
                  style={{
                    width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `1px solid ${COLORS.border}`, borderRadius: '8px',
                    cursor: btn.disabled ? 'not-allowed' : 'pointer',
                    backgroundColor: btn.disabled ? '#f3f4f6' : 'white',
                    color: btn.disabled ? '#d1d5db' : COLORS.text,
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!btn.disabled) {
                      e.currentTarget.style.backgroundColor = COLORS.primary;
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.borderColor = COLORS.primary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!btn.disabled) {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.color = COLORS.text;
                      e.currentTarget.style.borderColor = COLORS.border;
                    }
                  }}
                >
                  {btn.icon}
                </button>
              ))}
            </div>

            <span style={{ color: COLORS.textMuted, fontSize: '13px', fontWeight: '500' }}>
              Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: COLORS.textMuted, fontSize: '13px' }}>Rows per page:</span>
              <select
                value={table.getState().pagination.pageSize}
                onChange={(e) => table.setPageSize(Number(e.target.value))}
                onFocus={handleFocus}
                onBlur={handleBlur}
                style={{
                  ...inputBase,
                  width: 'auto',
                  padding: '6px 10px',
                  cursor: 'pointer',
                }}
              >
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          </div>

          {data.length === 0 && !loading && (
            <p style={{ textAlign: 'center', color: COLORS.textMuted, fontStyle: 'italic', padding: '40px 20px' }}>
              {emptyMessage}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export { HoverButton };
export default AdminTable;
